import React, { useEffect, useMemo, useState } from 'react';
import './CompetitorComparison.css';
import API_BASE_URL from '../config/apiBaseUrl';

const getAuthHeaders = (headers = {}) => {
  const token = localStorage.getItem('authToken');
  return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
};

const formatCurrency = (value) => `${Number(value || 0).toFixed(2)} EUR`;

const normalizeVendorKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const getPriceTone = (price, referencePrice) => {
  if (!referencePrice || referencePrice === 0) {
    return 'normal';
  }

  const percentDiff = ((price - referencePrice) / referencePrice) * 100;
  if (percentDiff < -15) {
    return 'low';
  }
  if (percentDiff > 15) {
    return 'high';
  }

  return 'normal';
};

const buildGroupedComparisons = (comparisons) => {
  const grouped = {};

  comparisons.forEach((comparison) => {
    const oemRef = comparison.oemReference;
    if (!grouped[oemRef]) {
      grouped[oemRef] = {
        oemReference: comparison.oemReference,
        ourPrice: comparison.ourPrice || 0,
        ourShippingCost: comparison.ourShippingCost || 0,
        ourTotalPrice: comparison.ourTotalPrice || 0,
        ebayMedianGlobal: comparison.ebayMedianGlobal || null,
        opistoNationalStock: comparison.opistoNationalStock ?? null,
        opistoAveragePrice: comparison.opistoAveragePrice ?? null,
        opistoImportedAt: comparison.opistoImportedAt || null,
        competitors: [
          {
            competitorName: 'Nos stocks',
            price: comparison.ourPrice || 0,
            shippingCost: comparison.ourShippingCost || 0,
            totalPrice: comparison.ourTotalPrice || 0,
            isOurPrice: true
          }
        ],
        vendorKeys: new Set()
      };
    }

    (comparison.competitors || []).forEach((vendor) => {
      if (vendor.isOpisto) {
        return;
      }

      const vendorKey = normalizeVendorKey(vendor.name || 'Vendeur inconnu');
      if (vendorKey === '' || grouped[oemRef].vendorKeys.has(vendorKey)) {
        return;
      }

      grouped[oemRef].vendorKeys.add(vendorKey);

      grouped[oemRef].competitors.push({
        competitorName: vendor.name || 'Vendeur inconnu',
        price: vendor.price || 0,
        shippingCost: vendor.shippingCost || 0,
        totalPrice: vendor.totalPrice || 0,
        stock: vendor.stock || 0,
        ebayMedianTotalPrice: vendor.ebayMedianTotalPrice || null
      });
    });
  });

  Object.values(grouped).forEach((entry) => {
    delete entry.vendorKeys;

    const competitorPrices = entry.competitors
      .filter((competitor) => !competitor.isOurPrice)
      .map((competitor) => competitor.totalPrice);

    if (competitorPrices.length === 0) {
      entry.avgPrice = null;
      entry.minPrice = null;
      entry.maxPrice = null;
      entry.priceDiff = null;
      entry.priceDiffPercent = null;
      entry.bestDeal = false;
      return;
    }

    const avgPrice = competitorPrices.reduce((sum, price) => sum + price, 0) / competitorPrices.length;
    entry.avgPrice = avgPrice;
    entry.minPrice = Math.min(...competitorPrices);
    entry.maxPrice = Math.max(...competitorPrices);
    entry.bestDeal = entry.ourTotalPrice < entry.minPrice;
    entry.priceDiff = entry.ourTotalPrice - avgPrice;
    entry.priceDiffPercent = avgPrice > 0 ? (entry.priceDiff / avgPrice) * 100 : null;
  });

  return grouped;
};

const CompetitorRow = ({ competitor, avgPrice }) => {
  const tone = competitor.isOurPrice ? 'our-price' : getPriceTone(competitor.totalPrice, avgPrice);

  return (
    <div className={`competitor-row ${tone}`}>
      <div className="competitor-main">
        <div className="competitor-name-row">
          <span className="competitor-name">{competitor.competitorName}</span>
          {competitor.isOurPrice && <span className="row-tag tag-ours">Interne</span>}
          {!competitor.isOurPrice && <span className="row-tag tag-ebay">eBay</span>}
        </div>
        <div className="competitor-meta">
          <span>Prix: {formatCurrency(competitor.price)}</span>
          <span>Port: {formatCurrency(competitor.shippingCost)}</span>
          {competitor.stock ? <span>Stock: {competitor.stock}</span> : null}
        </div>
      </div>

      <div className="competitor-side">
        {!competitor.isOurPrice && competitor.ebayMedianTotalPrice && (
          <span className="median-badge">
            Mediane vendeur: {formatCurrency(competitor.ebayMedianTotalPrice)}
          </span>
        )}
        <span className="total-price">{formatCurrency(competitor.totalPrice)}</span>
      </div>
    </div>
  );
};

const CompetitorComparison = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groupedData, setGroupedData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const fetchComparisons = async (rawSearchTerm, currentPage, currentRowsPerPage) => {
    const normalizedSearch = rawSearchTerm.trim();

    setLoading(true);
    setError(null);

    try {
      const query = new URLSearchParams();
      query.set('limit', String(currentRowsPerPage));
      query.set('offset', String(currentPage * currentRowsPerPage));

      if (normalizedSearch !== '') {
        query.set('oem', normalizedSearch);
      }

      const response = await fetch(`${API_BASE_URL}/comparison/all?${query.toString()}`, {
        method: 'GET',
        headers: getAuthHeaders({
          'Content-Type': 'application/json'
        }),
        timeout: 30000
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.comparisons) {
        setGroupedData(buildGroupedComparisons(data.comparisons));
        setTotalCount(data.total || 0);
        return;
      }

      setGroupedData({});
      setTotalCount(0);
    } catch (err) {
      setError(`Erreur de chargement: ${err.message}`);
      setGroupedData({});
      setTotalCount(0);
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const normalizedSearch = searchTerm.trim();
    const timeoutId = window.setTimeout(() => {
      fetchComparisons(searchTerm, page, rowsPerPage);
    }, normalizedSearch === '' ? 0 : 250);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm, page, rowsPerPage]);

  const entries = useMemo(() => Object.entries(groupedData), [groupedData]);
  const searchActive = searchTerm.trim() !== '';
  const visibleCount = entries.length;
  const totalPages = Math.max(Math.ceil(totalCount / rowsPerPage), 1);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(0);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/export/competitors-prices`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'export');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `competitor_prices_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Erreur lors de l\'export Excel');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="competitor-comparison">
      <section className="comparison-hero">
        <div className="hero-copy">
          <h1>Comparaison des prix concurrents</h1>
          <p className="hero-text">
            Comparez votre prix stock avec les vendeurs par refOEM, en vous appuyant sur les medianes prix + port.
          </p>
        </div>

        <div className="hero-actions">
          <label className="search-field">
            <span className="search-label">Recherche refOEM</span>
            <div className="search-control">
                <span className="search-icon">OEM</span>
              <input
                type="text"
                className="search-input"
                placeholder="Ex: 00007840AS"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchActive && (
                <button
                  type="button"
                  className="clear-btn"
                  onClick={handleClearSearch}
                >
                  Effacer
                </button>
              )}
            </div>
          </label>

          <button
            className="export-btn"
            onClick={handleExport}
            disabled={loading || visibleCount === 0}
          >
            Exporter en Excel
          </button>
        </div>
      </section>

      {error && <div className="error-message">{error}</div>}

      <section className="summary-strip">
        <div className="summary-card">
          <span className="summary-label">Resultats affiches</span>
          <strong>{visibleCount} / {totalCount}</strong>
        </div>
        <div className="summary-card">
          <span className="summary-label">Mode</span>
          <strong>{searchActive ? 'Recherche ciblee' : 'Vue generale'}</strong>
        </div>
      </section>

      {loading && visibleCount === 0 ? (
        <div className="loading-panel">Chargement des comparaisons...</div>
      ) : visibleCount === 0 ? (
        <div className="empty-panel">
          <h2>{searchActive ? 'Aucun resultat' : 'Aucune donnee disponible'}</h2>
          <p>
            {searchActive
              ? 'Aucune comparaison stocks vs concurrent ne correspond a cette ref.'
              : 'Les comparaisons n ont retourne aucune ligne exploitable pour le moment.'}
          </p>
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px',
              flexWrap: 'wrap'
            }}
          >
            <div style={{ fontSize: '0.95rem', color: '#5b6472' }}>
              Page {page + 1} sur {totalPages}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.95rem', color: '#5b6472' }}>Lignes par page</span>
                <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  {[25, 50, 100, 250].map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="clear-btn"
                onClick={() => setPage((currentPage) => Math.max(currentPage - 1, 0))}
                disabled={page === 0 || loading}
              >
                Precedent
              </button>
              <button
                type="button"
                className="export-btn"
                onClick={() => setPage((currentPage) => currentPage + 1)}
                disabled={loading || page + 1 >= totalPages}
              >
                Suivant
              </button>
            </div>
          </div>

          <div className="comparison-grid">
            {entries.map(([oemRef, data]) => (
              <article key={oemRef} className="comparison-card">
                <div className="comparison-card-top">
                  <div>
                    <p className="card-kicker">Reference OEM</p>
                    <h2>{oemRef}</h2>
                  </div>

                  <div className="price-signal">
                    <span className={`signal-badge ${data.bestDeal ? 'signal-good' : 'signal-watch'}`}>
                      {data.bestDeal ? 'Competitif' : 'A surveiller'}
                    </span>
                  </div>
                </div>

                <div className="metrics-grid">
                  <div className="metric-box">
                    <span className="metric-label">Notre total</span>
                    <strong>{formatCurrency(data.ourTotalPrice)}</strong>
                  </div>
                  <div className="metric-box">
                    <span className="metric-label">Moyenne eBay</span>
                    <strong>{data.avgPrice ? formatCurrency(data.avgPrice) : 'N/A'}</strong>
                  </div>
                  <div className="metric-box">
                    <span className="metric-label">Mediane eBay</span>
                    <strong>{data.ebayMedianGlobal ? formatCurrency(data.ebayMedianGlobal) : 'N/A'}</strong>
                  </div>
                  <div className="metric-box">
                    <span className="metric-label">Ecart vs moyenne</span>
                    <strong className={data.priceDiff !== null && data.priceDiff < 0 ? 'metric-positive' : 'metric-negative'}>
                      {data.priceDiff !== null && data.priceDiffPercent !== null
                        ? `${formatCurrency(Math.abs(data.priceDiff))} (${Math.abs(data.priceDiffPercent).toFixed(1)}%)`
                        : 'N/A'}
                    </strong>
                  </div>
                  <div className="metric-box">
                    <span className="metric-label">Stock national Opisto</span>
                    <strong>{data.opistoNationalStock != null ? data.opistoNationalStock : 'N/A'}</strong>
                  </div>
                  <div className="metric-box">
                    <span className="metric-label">Prix moyen Opisto</span>
                    <strong>{data.opistoAveragePrice != null ? formatCurrency(data.opistoAveragePrice) : 'N/A'}</strong>
                  </div>
                </div>

                <div className="section-title-row">
                  <h3>Comparaison par vendeur</h3>
                  <span>{data.competitors.length} lignes</span>
                </div>

                <div className="competitors-list">
                  {data.competitors.map((competitor, index) => (
                    <CompetitorRow
                      key={`${oemRef}-${competitor.competitorName}-${index}`}
                      competitor={competitor}
                      avgPrice={data.avgPrice}
                    />
                  ))}
                </div>
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CompetitorComparison;
